import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, PaperPlaneRight, ChatCircle, Minus } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { playMessageSound } from "../lib/notify";
import { containsProfanity } from "../lib/profanity";

function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch { return ""; }
}

/**
 * Floating chat drawer — Portal-based so it's not affected by parent stacking.
 * Modes: open (full panel) | minimized (small bubble at corner)
 */
export default function ChatDrawer({ booking, onClose, readOnly = false }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const endRef = useRef(null);
  const lastCountRef = useRef(0);
  const initialLoadRef = useRef(true);

  const load = async (silent = false) => {
    try {
      const { data } = await api.get(`/bookings/${booking.id}/chat`);
      if (!initialLoadRef.current && data.length > lastCountRef.current) {
        const newOnes = data.slice(lastCountRef.current);
        const hasIncoming = newOnes.some((m) => m.from_user_id !== user?.id);
        if (hasIncoming && !minimized && document.visibilityState === "visible") playMessageSound();
      }
      lastCountRef.current = data.length;
      initialLoadRef.current = false;
      setMessages(data);
    } catch (e) {
      if (!silent) toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
    // V7.1: short polling 5s enquanto o chat estiver aberto e a aba visível
    let t = null;
    const start = () => {
      if (t) return;
      t = setInterval(() => { if (document.visibilityState === "visible") load(true); }, 5000);
    };
    const stop = () => { if (t) { clearInterval(t); t = null; } };
    start();
    const onVis = () => { if (document.visibilityState === "visible") { load(true); start(); } else { stop(); } };
    const onFocus = () => { if (document.visibilityState === "visible") load(true); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.id]);

  // Lock body scroll ONLY when open (not minimized) on mobile.
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (!minimized && isMobile) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [minimized]);

  useEffect(() => {
    if (!minimized) endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, minimized]);

  const send = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t || busy) return;
    if (containsProfanity(t)) {
      toast.error("Sua mensagem contém linguagem ofensiva. Reformule para manter o respeito entre as partes.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.id}/chat`, { text: t });
      setText("");
      load(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const otherName = booking.user_id === user?.id ? booking.owner_name : booking.user_name;
  const unreadCount = messages.filter((m) => m.from_user_id !== user?.id && !m.read_at).length;

  const content = minimized ? (
    // MINIMIZED — small floating bubble bottom-right that doesn't block UI
    <button
      onClick={() => setMinimized(false)}
      className="fixed bottom-20 right-4 z-[80] bg-[#004E98] hover:bg-[#003A73] text-white rounded-full shadow-2xl p-4 flex items-center gap-2 transition-transform hover:scale-105"
      style={{ paddingBottom: "max(env(safe-area-inset-bottom, 0px), 1rem)" }}
      data-testid="chat-bubble-minimized"
      aria-label="Abrir chat"
    >
      <ChatCircle size={24} weight="fill" />
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow ring-2 ring-white">
          {unreadCount > 9 ? "9+" : unreadCount}
        </span>
      )}
    </button>
  ) : (
    // OPEN — full panel (mobile: bottom sheet, desktop: floating card)
    <>
      {/* Backdrop ONLY on mobile */}
      <div
        className="fixed inset-0 z-[70] bg-black/50 md:hidden"
        onClick={(e) => e.target === e.currentTarget && setMinimized(true)}
        data-testid="chat-backdrop"
      />
      <div
        className="fixed z-[75] bg-white text-[#0F172A] flex flex-col shadow-2xl overflow-hidden inset-x-0 bottom-0 md:inset-auto md:bottom-6 md:right-6 md:w-[400px] rounded-t-2xl md:rounded-2xl"
        style={{
          height: window.innerWidth >= 768 ? "560px" : "min(85vh, 640px)",
          maxHeight: "95vh",
        }}
        data-testid="chat-drawer"
      >
        <div className="border-b border-slate-200 p-3 flex items-center justify-between shrink-0 bg-gradient-to-r from-[#004E98] to-[#003A73] text-white">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <ChatCircle size={20} weight="duotone" color="#FFCC00" />
            <div className="min-w-0">
              <div className="font-heading font-bold text-sm truncate">{otherName || "Conversa"}</div>
              <div className="text-[10px] text-white/70 truncate">{booking.listing_title}</div>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => setMinimized(true)}
              className="p-2 -mr-1 rounded-full hover:bg-white/10 active:bg-white/20"
              data-testid="chat-minimize-btn"
              aria-label="Minimizar"
            >
              <Minus size={20} weight="bold" />
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/10 active:bg-white/20"
              data-testid="chat-close-btn"
              aria-label="Fechar chat"
            >
              <X size={22} weight="bold" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain p-3 space-y-2 bg-slate-50" data-testid="chat-messages">
          {messages.length === 0 ? (
            <div className="text-center text-xs text-slate-400 py-8">
              Sem mensagens ainda — envie a primeira. <br />
              <span className="text-[10px]">Todas as mensagens ficam registradas para fins de mediação.</span>
            </div>
          ) : (
            messages.map((m) => {
              const mine = m.from_user_id === user?.id;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm shadow-sm ${mine ? "bg-[#004E98] text-white rounded-br-sm" : "bg-white text-slate-800 rounded-bl-sm border border-slate-200"}`}
                    data-testid={`chat-message-${m.id}`}
                  >
                    <div className="whitespace-pre-wrap break-words">{m.text}</div>
                    <div className={`text-[9px] mt-1 ${mine ? "text-white/70" : "text-slate-400"} text-right`}>{formatTime(m.sent_at)}</div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={endRef} />
        </div>

        <form
          onSubmit={send}
          className="border-t border-slate-200 p-2 flex items-center gap-2 shrink-0 bg-white"
          style={{ paddingBottom: "max(env(safe-area-inset-bottom, 0px), 0.5rem)" }}
        >
          {readOnly ? (
            <div className="flex-1 text-center text-[11px] text-slate-500 py-2 italic" data-testid="chat-readonly">
              🔒 Esta conversa foi encerrada. Histórico disponível apenas para leitura.
            </div>
          ) : (
            <>
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Mensagem..."
                className="aj-input flex-1"
                data-testid="chat-input"
                maxLength={1000}
              />
              <button
                type="submit"
                disabled={!text.trim() || busy}
                className="bg-[#004E98] hover:bg-[#003A73] disabled:opacity-50 text-white p-2.5 rounded-full transition shrink-0"
                data-testid="chat-send-btn"
                aria-label="Enviar"
              >
                <PaperPlaneRight size={18} weight="bold" />
              </button>
            </>
          )}
        </form>
      </div>
    </>
  );

  // Portal renders directly to body — escapes any stacking/overflow issues from parents.
  return createPortal(content, document.body);
}
